/**
 * One of Harper's posts in the sheet: the Update as it posted, why it ran,
 * what Harper read, and when. Read from `GET /api/workers/harper/runs/:runId`.
 *
 * Harper's run model has no post that waits for a person: `held` is a
 * capacity hold that resumes on its own, and the urgent text that used to
 * wait for approval left the product with SMS. So the sheet shows posted
 * Updates only, and its footer has no Approve. A run record carries the
 * channel's id but no Slack permalink, so there is no "Open in Slack" verb.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Fold, Pill, RecordHead, SheetFoot, Chip } from "../queue/index.js";
import { HarperUpdateView, degradedSentences, reportSummary } from "../work/harperUpdate.js";
import { useWorkerLookup } from "../work/useWorkerLookup.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import type { HarperRunDetail } from "../workers/useWorkers.js";
import type { ReviewsApi } from "../reviews/reviewsApi.js";

export const HARPER_SLUG = "harper";

function clock(iso: string | null | undefined): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export interface PostSheetProps {
  api: ReviewsApi;
  runId: string;
  teamName: string;
  /** The next row in the fold, or null when this is the last. */
  onNext: (() => void) | null;
  onClose: () => void;
}

export function PostSheet({ api, runId, teamName, onNext, onClose }: PostSheetProps) {
  const harper = useWorkerLookup()(HARPER_SLUG);
  const [detail, setDetail] = useState<HarperRunDetail | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api
      .harperRun(runId)
      .then((body) => {
        if (cancelled) return;
        setDetail(body);
        setStatus(body ? "ready" : "missing");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [api, runId]);

  const run = detail?.run ?? null;
  const job = detail?.job ?? null;
  const kind = job?.name ?? run?.jobName ?? "Post";
  const kick = (
    <span className="stagebtn" style={{ "--stage": "var(--agent-comms)" } as CSSProperties}>
      {harper && <WorkerMark avatar={harper.avatar} name={harper.displayName} slug={harper.slug} size="sm" />}
      {kind}
    </span>
  );

  if (!run) {
    return (
      <>
        <RecordHead recordKey="HARPER" kick={kick} title={status === "loading" ? "Reading the post" : "Post"} noExpand onClose={onClose} />
        <div className="sbody">
          <p className="body-p" role={status === "error" ? "alert" : undefined}>
            {status === "loading" ? "Reading what Harper posted." : status === "missing" ? "This post is gone." : "Could not read this post just now."}
          </p>
        </div>
      </>
    );
  }

  const postedAt = run.deliveredAt ?? run.createdAt;
  const channel = job?.delivery ? (job.delivery.kind === "slack" ? "Slack" : job.delivery.kind) : null;
  const report = run.report ?? null;
  const degraded = degradedSentences(report);

  return (
    <>
      <RecordHead
        recordKey="HARPER"
        kick={kick}
        title={run.update?.headline ?? kind}
        onClose={onClose}
        meta={
          <>
            <Pill kind="done" dot={false}>
              Posted
            </Pill>
            {channel && <Chip>{channel}</Chip>}
            <Chip>{teamName}</Chip>
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
              posted {clock(postedAt)}
            </span>
          </>
        }
      />
      <div className="sbody" data-testid="post-sheet">
        <div className="blk">
          <div className="bh">
            <span className="label">As posted</span>
          </div>
          {run.update ? (
            <div className="brief">
              <HarperUpdateView update={run.update} />
            </div>
          ) : (
            <p className="body-p">This run recorded no Update text.</p>
          )}
        </div>
        <div className="blk">
          <div className="bh">
            <span className="label">Why it posted</span>
          </div>
          <p className="body-p">
            {kind} ran for {clock(run.windowStart)} to {clock(run.windowEnd)}.{job?.instructions ? ` Its instructions: ${job.instructions}` : ""}
          </p>
        </div>
        <div>
          <Fold title="What Harper read">
            {report ? <p>{reportSummary(report)}</p> : <p>This run recorded no reading report.</p>}
            {degraded.map((sentence) => (
              <p key={sentence}>{sentence}</p>
            ))}
          </Fold>
          <Fold title="History">
            <p>
              Started {clock(run.createdAt)}. Posted {clock(postedAt)}
              {channel ? ` to ${channel}` : ""}.
            </p>
          </Fold>
        </div>
      </div>
      <SheetFoot
        why={`Posted ${clock(postedAt)}. Nothing here can be changed.`}
        primary={{ label: "Next item", onClick: () => (onNext ? onNext() : onClose()) }}
      />
    </>
  );
}
